import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { RootState } from '../../../store/store';
import { initialsOf, useLearnerContext } from './useLearnerContext';

/** What the console calls the parent's own view ("Me", PRT-08-FR-08). */
export const PARENT_VIEW_LABEL = 'Vista de padre';

/**
 * PRT-17-FR-02 — the family console's statement of whose view this is.
 *
 * Sits in the top bar so it is visible on every route and at every width: the
 * switcher below lives in the rail, which a phone only shows as a drawer.
 */
export const FamilyContextIndicator: React.FC = () => {
  // Selectors rather than the hook: the indicator needs no student list.
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const activeContext = useSelector((state: RootState) => state.context.activeContext);
  if (role !== 'PARENT') return null;

  return (
    <span className="chip" role="status" aria-label="Contexto actual">
      {activeContext ? `Viendo como ${activeContext.studentName}` : PARENT_VIEW_LABEL}
    </span>
  );
};

/**
 * PRT-08-FR-07 — the learner switcher, in the family console's own markup.
 *
 * The console used to have none: the only switcher lived in MainLayout's
 * sidebar, so a parent on `/parent/*` could neither see nor change which child
 * they were acting for. The classes (`.sw`, `.sw-btn`, `.sw-item`) are the
 * redesign prototype's profile switcher; the behaviour is the shared hook's.
 *
 * "Ver como padre" is always listed (PRT-BR-P02-09: "Me" is a context of its
 * own, not the absence of one) and clears the server-side record, so a refresh
 * cannot bring the learner back.
 */
const FamilyContextSwitcher: React.FC<{ parentInitials: string }> = ({ parentInitials }) => {
  const {
    isParent,
    activeContext,
    isSwitching,
    error,
    students,
    loaded,
    fetchError,
    select,
    returnToParent,
  } = useLearnerContext();

  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  if (!isParent) return null;

  const hasActive = Boolean(activeContext);

  const chooseParent = async () => {
    if (!hasActive) {
      setIsOpen(false);
      return;
    }
    // Stays open on failure so the reason below is seen.
    if (await returnToParent()) setIsOpen(false);
  };

  const chooseLearner = (studentId: string) => {
    select(studentId);
    setIsOpen(false);
  };

  return (
    <div className={`sw${isOpen ? ' open' : ''}`} ref={ref}>
      <button
        className="sw-btn"
        onClick={() => setIsOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Cambiar alumno. Vista actual: ${
          activeContext?.studentName ?? PARENT_VIEW_LABEL
        }`}
      >
        <span className={`sw-av${hasActive ? '' : ' parent'}`}>
          {hasActive ? initialsOf(activeContext!.studentName) : parentInitials}
        </span>
        <span className="sw-info">
          <span className="sw-name">{activeContext?.studentName ?? PARENT_VIEW_LABEL}</span>
          <span className="sw-sub">{hasActive ? 'Alumno activo' : 'Toda la familia'}</span>
        </span>
        <span className="sw-chev" aria-hidden="true">
          ▾
        </span>
      </button>

      {isOpen && (
        <div className="sw-list" role="listbox" aria-label="Seleccionar alumno">
          <button
            type="button"
            role="option"
            aria-selected={!hasActive}
            className={`sw-item${hasActive ? '' : ' on'}`}
            onClick={chooseParent}
            disabled={isSwitching}
          >
            <span className="em p">{parentInitials}</span>
            Ver como padre
            {!hasActive && <span className="chk">✓</span>}
          </button>

          <div className="sw-sep" />

          {!loaded && !fetchError ? (
            <p className="sw-sub" role="status">
              Cargando alumnos…
            </p>
          ) : fetchError ? (
            <p className="sw-sub">Error al cargar alumnos</p>
          ) : students.length === 0 ? (
            <p className="sw-sub">Todavía no tienes alumnos</p>
          ) : (
            students.map((s) => {
              const name = `${s.firstName} ${s.lastName}`;
              const isActive = activeContext?.studentId === s.studentId;
              return (
                <button
                  type="button"
                  key={s.studentId}
                  role="option"
                  aria-selected={isActive}
                  className={`sw-item${isActive ? ' on' : ''}`}
                  onClick={() => chooseLearner(s.studentId)}
                  disabled={isSwitching}
                >
                  <span className="em">{initialsOf(name)}</span>
                  {name}
                  {isActive && <span className="chk">✓</span>}
                </button>
              );
            })
          )}

          {error && (
            <p className="sw-sub" role="alert">
              No pudimos cambiar la vista. Inténtalo de nuevo.
            </p>
          )}

          <div className="sw-sep" />

          <Link to="/parent/students/new" className="sw-item add" onClick={() => setIsOpen(false)}>
            <span className="em">+</span>
            Agregar alumno
          </Link>
          {/* GAP-S01-01 — hand the device to a child: their space, behind the Parent's re-auth. */}
          <Link to="/parent/kid-mode" className="sw-item add" onClick={() => setIsOpen(false)}>
            <span className="em">★</span>
            Modo niño
          </Link>
        </div>
      )}
    </div>
  );
};

export default FamilyContextSwitcher;
